import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import CategoryCombobox, { useTagCategories } from "@/components/CategoryCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, Loader2, Pencil, MoreHorizontal, Power, PowerOff, Trash2,
  Tags as TagsIcon, Layers, CheckCircle2, XCircle,
} from "lucide-react";
import { useConfirm } from "@/hooks/useConfirm";

const PRESET_COLORS = [
  "#25D366", "#10B981", "#F59E0B", "#EF4444",
  "#8B5CF6", "#0EA5E9", "#EC4899", "#64748B",
];

const emptyForm = { name: "", color: "#25D366", category: "General", is_active: true };

export default function Tags() {
  const { categories, reload: reloadCategories } = useTagCategories();
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, category_count: 0 });
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (activeFilter === "active") params.active = true;
      else if (activeFilter === "inactive") params.active = false;
      const [tagsRes, statsRes] = await Promise.all([
        api.get("/tags", { params }),
        api.get("/tags/stats"),
      ]);
      setTags(tagsRes.data);
      setStats(statsRes.data);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, activeFilter]);

  useEffect(() => { load(); }, [load]);

  const filteredTags = useMemo(() => {
    if (!search.trim()) return tags;
    const q = search.toLowerCase();
    return tags.filter(
      (t) => t.name.toLowerCase().includes(q) || (t.category || "").toLowerCase().includes(q)
    );
  }, [tags, search]);

  const openCreateDialog = () => {
    setForm(emptyForm);
    setEditing(null);
    setOpenCreate(true);
  };

  const openEditDialog = (tag) => {
    setForm({
      name: tag.name,
      color: tag.color,
      category: tag.category || "General",
      is_active: tag.is_active !== false,
    });
    setEditing(tag);
    setOpenCreate(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Tag name required");
    if (!form.category.trim()) return toast.error("Category required");
    try {
      if (editing) {
        await api.patch(`/tags/${editing.id}`, form);
        toast.success("Tag updated");
      } else {
        await api.post("/tags", form);
        toast.success("Tag created");
      }
      setOpenCreate(false);
      setForm(emptyForm);
      setEditing(null);
      load();
      reloadCategories();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save tag");
    }
  };

  const toggleActive = async (tag) => {
    try {
      await api.patch(`/tags/${tag.id}`, { is_active: !tag.is_active });
      toast.success(tag.is_active ? "Tag disabled" : "Tag enabled");
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const removeTag = async (tag) => {
    const ok = await confirm({
      title: `Delete tag "${tag.name}"?`,
      description:
        tag.contact_count > 0
          ? `This tag is assigned to ${tag.contact_count} contact${tag.contact_count !== 1 ? "s" : ""}. It will be removed from all of them. (Tip: disable instead to preserve history.)`
          : "This will permanently remove the tag.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/tags/${tag.id}`);
    toast.success("Tag deleted");
    load();
    reloadCategories();
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="tags-page">
      <PageHeader
        title="Tags"
        description="Organize contacts with categorized, toggleable tags."
        actions={
          <Button onClick={openCreateDialog} className="bg-brand hover:bg-brand-dark" data-testid="tag-new-button">
            <Plus className="w-4 h-4 mr-1.5" />New tag
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard icon={TagsIcon} label="Total tags" value={stats.total} testId="tag-stat-total" />
        <StatCard icon={CheckCircle2} label="Active" value={stats.active} tone="text-emerald-600" testId="tag-stat-active" />
        <StatCard icon={XCircle} label="Inactive" value={stats.inactive} tone="text-slate-400" testId="tag-stat-inactive" />
        <StatCard icon={Layers} label="Categories" value={stats.category_count} tone="text-sky-600" testId="tag-stat-categories" />
      </div>

      <Tabs defaultValue="tags" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="tags" data-testid="tab-tags">Tags</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="tags" className="mt-0">
          <Card className="border-slate-200 shadow-none">
            <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-2">
              <Input
                placeholder="Search tag name or category…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs"
                data-testid="tag-search-input"
              />
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-52" data-testid="tag-category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.filter((c) => c.is_active !== false).map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={activeFilter} onValueChange={setActiveFilter}>
                <SelectTrigger className="w-40" data-testid="tag-active-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active only</SelectItem>
                  <SelectItem value="inactive">Inactive only</SelectItem>
                </SelectContent>
              </Select>
              <div className="ml-auto text-xs text-slate-400" data-testid="tag-result-count">
                {filteredTags.length} {filteredTags.length === 1 ? "tag" : "tags"}
              </div>
            </div>

            <CardContent className="p-0">
              {loading ? (
                <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
              ) : filteredTags.length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-400">No tags match the current filters.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Tag</th>
                        <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Category</th>
                        <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Color</th>
                        <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Contacts</th>
                        <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Status</th>
                        <th className="text-right text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTags.map((t) => (
                        <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tag-row-${t.id}`}>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                              <span className={`font-medium ${t.is_active ? "text-slate-800" : "text-slate-400 line-through"}`}>
                                {t.name}
                              </span>
                            </div>
                          </td>
                          <td className="p-3 text-slate-600" data-testid={`tag-category-${t.id}`}>{t.category || "General"}</td>
                          <td className="p-3 font-mono text-xs text-slate-500">{t.color}</td>
                          <td className="p-3" data-testid={`tag-count-${t.id}`}>
                            <span className="inline-flex items-center bg-slate-100 text-slate-700 rounded-md px-2 py-0.5 text-xs">
                              {t.contact_count ?? 0}
                            </span>
                          </td>
                          <td className="p-3">
                            {t.is_active ? (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`tag-status-${t.id}`}>Active</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200" data-testid={`tag-status-${t.id}`}>Inactive</Badge>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <div className="inline-flex items-center gap-1">
                              <Button size="sm" variant="outline" onClick={() => openEditDialog(t)} data-testid={`tag-edit-${t.id}`} className="h-8">
                                <Pencil className="w-3.5 h-3.5 mr-1" />Edit
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="outline" className="h-8" data-testid={`tag-more-${t.id}`}>
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {t.is_active ? (
                                    <DropdownMenuItem onClick={() => toggleActive(t)} data-testid={`tag-disable-${t.id}`}>
                                      <PowerOff className="w-4 h-4 mr-2" />Disable
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => toggleActive(t)} data-testid={`tag-enable-${t.id}`}>
                                      <Power className="w-4 h-4 mr-2 text-emerald-600" />Enable
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => removeTag(t)} className="text-red-600" data-testid={`tag-delete-${t.id}`}>
                                    <Trash2 className="w-4 h-4 mr-2" />Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="mt-0">
          <CategoryManager categories={categories} reload={reloadCategories} reloadTags={load} />
        </TabsContent>
      </Tabs>

      {/* Create / Edit tag dialog */}
      <Dialog open={openCreate} onOpenChange={(o) => { setOpenCreate(o); if (!o) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit tag" : "New tag"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="VIP, Newsletter, …"
                className="mt-1.5"
                data-testid="tag-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Category</Label>
              <div className="mt-1.5">
                <CategoryCombobox
                  value={form.category}
                  onChange={(v) => setForm({ ...form, category: v })}
                  categories={categories}
                  onCategoriesChange={reloadCategories}
                  triggerTestId="tag-category-combobox"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Color</Label>
              <div className="flex gap-2 mt-2 items-center">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    data-testid={`tag-color-${c}`}
                    className={`w-7 h-7 rounded-full border-2 transition-transform ${form.color === c ? "border-slate-900 scale-110" : "border-transparent"}`}
                    style={{ backgroundColor: c }}
                    aria-label={`Choose ${c}`}
                  />
                ))}
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-7 h-7 rounded border border-slate-200 cursor-pointer"
                  data-testid="tag-color-custom"
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <div>
                <Label className="text-sm font-medium text-slate-800">Active</Label>
                <p className="text-xs text-slate-500">Inactive tags are hidden from contact pickers and flows.</p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="tag-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenCreate(false)} data-testid="tag-cancel-button">Cancel</Button>
            <Button onClick={submit} className="bg-brand hover:bg-brand-dark" data-testid="tag-submit-button">
              {editing ? "Save changes" : "Create tag"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================ Category Manager ============================
function CategoryManager({ categories, reload, reloadTags }) {
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", is_active: true });
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  const openCreateDialog = () => {
    setForm({ name: "", is_active: true });
    setEditing(null);
    setOpenCreate(true);
  };

  const openEditDialog = (cat) => {
    setForm({ name: cat.name, is_active: cat.is_active !== false });
    setEditing(cat);
    setOpenCreate(true);
  };

  const submit = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Category name required");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/tag-categories/${editing.id}`, { name, is_active: form.is_active });
        toast.success("Category updated");
      } else {
        await api.post("/tag-categories", { name, is_active: form.is_active });
        toast.success("Category created");
      }
      setOpenCreate(false);
      reload();
      reloadTags?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c) => {
    await api.patch(`/tag-categories/${c.id}`, { is_active: !c.is_active });
    toast.success(c.is_active ? "Category disabled" : "Category enabled");
    reload();
  };

  const remove = async (c) => {
    const ok = await confirm({
      title: `Delete category "${c.name}"?`,
      description: c.tag_count
        ? `${c.tag_count} tag${c.tag_count !== 1 ? "s" : ""} in this category will be moved to "General".`
        : "This will permanently remove the category.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/tag-categories/${c.id}`);
      toast.success("Category deleted");
      reload();
      reloadTags?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete category");
    }
  };

  return (
    <Card className="border-slate-200 shadow-none" data-testid="category-manager">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Categories</h3>
          <p className="text-xs text-slate-500 mt-0.5">Manage the categories used to group your tags.</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-brand hover:bg-brand-dark" data-testid="category-new-button">
          <Plus className="w-4 h-4 mr-1.5" />New category
        </Button>
      </div>
      <CardContent className="p-0">
        {categories.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No categories yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Category</th>
                  <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Tags</th>
                  <th className="text-left text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Status</th>
                  <th className="text-right text-xs font-semibold tracking-wider uppercase text-slate-500 p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`category-row-${c.id}`}>
                    <td className="p-3 font-medium text-slate-800">{c.name}</td>
                    <td className="p-3">
                      <span className="inline-flex items-center bg-slate-100 text-slate-700 rounded-md px-2 py-0.5 text-xs" data-testid={`category-tag-count-${c.id}`}>
                        {c.tag_count ?? 0}
                      </span>
                    </td>
                    <td className="p-3">
                      {c.is_active ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200">Inactive</Badge>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-8" onClick={() => openEditDialog(c)} data-testid={`category-edit-${c.id}`}>
                          <Pencil className="w-3.5 h-3.5 mr-1" />Edit
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="h-8" data-testid={`category-more-${c.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {c.is_active ? (
                              <DropdownMenuItem onClick={() => toggle(c)} data-testid={`category-disable-${c.id}`}>
                                <PowerOff className="w-4 h-4 mr-2" />Disable
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => toggle(c)} data-testid={`category-enable-${c.id}`}>
                                <Power className="w-4 h-4 mr-2 text-emerald-600" />Enable
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => remove(c)}
                              className="text-red-600"
                              disabled={c.name === "General"}
                              data-testid={`category-delete-${c.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={openCreate} onOpenChange={(o) => { setOpenCreate(o); if (!o) { setEditing(null); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit category" : "New category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">Name</Label>
              <Input
                autoFocus
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Lead Status, Campaign Source…"
                className="mt-1.5"
                data-testid="category-name-input"
              />
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <div>
                <Label className="text-sm font-medium text-slate-800">Active</Label>
                <p className="text-xs text-slate-500">Disabled categories are hidden from tag pickers.</p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="category-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenCreate(false)} data-testid="category-cancel-button">Cancel</Button>
            <Button onClick={submit} className="bg-brand hover:bg-brand-dark" disabled={saving} data-testid="category-submit-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Save changes" : "Create category")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value, tone = "text-slate-700", testId }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 flex items-center gap-3 bg-white" data-testid={testId}>
      <div className="w-9 h-9 rounded-md bg-slate-50 grid place-items-center">
        <Icon className={`w-4 h-4 ${tone}`} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-semibold text-slate-900 leading-tight">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
}
